/**
 * TemplateAnalysisView — Generic, template-driven analysis renderer.
 *
 * Reads the template's display.sections to determine what to render and how.
 * Each section type maps to a specialized sub-component.
 * Falls back to the existing AnalysisView for the "meeting-intelligence" template.
 */

import type { IDisplaySection } from "@lexicanon/shared"
import { Trans } from "@lingui/react/macro"
import { Alert, Stack } from "@mantine/core"
import type { IEditContext } from "./interfaces"
import { BadgesSection } from "./sections/BadgesSection"
import { GridSection } from "./sections/GridSection"
import { ImageGallerySection } from "./sections/ImageGallerySection"
import { ListSection } from "./sections/ListSection"
import { TaggedListSection } from "./sections/TaggedListSection"
import { TextSection } from "./sections/TextSection"

// IEditContext lives in ./interfaces so sections/ can import it without a cycle
// (index renders the sections). Re-exported here so external consumers keep
// importing it from "../TemplateAnalysisView".
export type { IEditContext } from "./interfaces"

interface IProps {
  /** Parsed analysis JSON from the backend */
  analysis: Record<string, unknown>
  /** Template display sections (from the template definition) */
  sections: IDisplaySection[]
  /** Optional artifacts (images, etc.) for gallery sections */
  artifacts?: Array<{ content: { url: string }; stepId: string }>
  /** Optional edit context — when provided, fields become editable */
  editContext?: IEditContext
}

/**
 * Resolve a dotted path like "mood.tension_arc" into nested object data.
 */
function resolvePath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split(".")
  let current: unknown = obj
  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[part]
  }
  return current
}

export function TemplateAnalysisView({ analysis, sections, artifacts, editContext }: IProps): React.ReactElement {
  const visibleSections = sections.filter((s) => s.visible)

  if (visibleSections.length === 0) {
    return (
      <Alert color="gray">
        <Trans>No display sections configured for this template.</Trans>
      </Alert>
    )
  }

  return (
    <Stack gap="xl">
      {visibleSections.map((section) => {
        const data = resolvePath(analysis, section.dataPath)
        return renderSection(section, data, artifacts, editContext)
      })}
    </Stack>
  )
}

function renderSection(
  section: IDisplaySection,
  data: unknown,
  artifacts?: Array<{ content: { url: string }; stepId: string }>,
  editContext?: IEditContext,
): React.ReactElement | null {
  switch (section.type) {
    case "text":
      return <TextSection key={section.id} section={section} data={data} editContext={editContext} />
    case "list":
      return <ListSection key={section.id} section={section} data={data} editContext={editContext} />
    case "tagged_list":
      return <TaggedListSection key={section.id} section={section} data={data} editContext={editContext} />
    case "grid":
      return <GridSection key={section.id} section={section} data={data} />
    case "badges":
      return <BadgesSection key={section.id} section={section} data={data} />
    case "image_gallery":
      return <ImageGallerySection key={section.id} section={section} data={data} artifacts={artifacts} />
    default:
      return null
  }
}
